import React, { useState } from 'react';
import { Sidebar, UserRole } from './components/Sidebar';
import { StatsRow } from './components/StatsRow';
import { DashboardOpportunitiesMap } from './components/DashboardOpportunitiesMap';
import { ActivityFeed } from './components/ActivityFeed';
import { PackageDetail } from './components/PackageDetail';
import { TeamPage } from './components/TeamPage';
import { TeamMemberProfile } from './components/TeamMemberProfile';
import { MyJobsPage } from './components/MyJobsPage';
import { EarningsPage } from './components/EarningsPage';
import { SettingsPage } from './components/SettingsPage';
import { OpportunitiesPage } from './components/OpportunitiesPage';
import { UnifiedMapPage } from './components/UnifiedMapPage';
import { PlannerPage } from './components/PlannerPage';
import { HostPlaceholderPage } from './components/HostPlaceholderPage';
import { HostPropertiesPage } from './components/HostPropertiesPage';
import { HostEsigenzePage } from './components/HostEsigenzePage';
import { HostMonitoraggioPage } from './components/HostMonitoraggioPage';
import { HostCalendarioPage } from './components/HostCalendarioPage';
import { HostBillingPage } from './components/HostBillingPage';
import { HostSettingsPage } from './components/HostSettingsPage';
import { UpcomingJobsList } from './components/UpcomingJobsList';
import { Receipt, Settings, Menu } from 'lucide-react';
export function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activePage, setActivePage] = useState('dashboard');
  const [userRole, setUserRole] = useState<UserRole>('cleaner');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const renderContent = () => {
    // HOST PAGES
    if (userRole === 'host') {
      switch (activePage) {
        case 'host-properties':
          return <HostPropertiesPage data-id="element-0" />;
        case 'host-esigenze':
          return <HostEsigenzePage data-id="element-1" />;
        case 'host-monitoraggio':
          return <HostMonitoraggioPage data-id="element-2" />;
        case 'host-calendario':
          return <HostCalendarioPage data-id="element-3" />;
        case 'host-billing':
          return <HostBillingPage data-id="element-4" />;
        case 'host-settings':
          return <HostSettingsPage data-id="element-5" />;
        default:
          return <HostPropertiesPage data-id="element-6" />;
      }
    }
    // CLEANER PAGES
    if (activePage === 'map') return <UnifiedMapPage data-id="element-7" />;
    if (activePage === 'planner') return <PlannerPage key="planner-calendar" onViewChange={view => {
      if (view === 'map') setActivePage('planner-map');
    }} data-id="element-8" />;
    if (activePage === 'planner-map') return <PlannerPage key="planner-map" initialView="map" onViewChange={view => {
      if (view === 'calendar') setActivePage('planner');
    }} data-id="element-9" />;
    if (activePage === 'packages') {
      return <OpportunitiesPage onNavigateToDetail={id => setActivePage('package-detail')} data-id="element-10" />;
    }
    if (activePage === 'package-detail') {
      return <PackageDetail onBack={() => setActivePage('packages')} data-id="element-11" />;
    }
    if (activePage === 'team') {
      return <TeamPage onViewProfile={id => setActivePage('team-profile')} data-id="element-12" />;
    }
    if (activePage === 'team-profile') {
      return <TeamMemberProfile onBack={() => setActivePage('team')} data-id="element-13" />;
    }
    if (activePage === 'jobs') return <MyJobsPage data-id="element-14" />;
    if (activePage === 'earnings' || activePage === 'billing') return <EarningsPage data-id="element-15" />;
    if (activePage === 'settings') return <SettingsPage data-id="element-16" />;
    // Default Dashboard View
    return <>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6" data-id="element-17">
          <div data-id="element-18">
            <h1 className="text-xl lg:text-2xl font-bold font-montserrat text-navy mb-1" data-id="element-19">
              Panoramica
            </h1>
            <p className="text-sm text-gray-500" data-id="element-20">
              Buongiorno Sofia, ecco il riepilogo di oggi
            </p>
          </div>
        </div>

        <StatsRow data-id="element-21" />

        <DashboardOpportunitiesMap onNavigateToOpportunities={() => setActivePage('planner-map')} data-id="element-22" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-id="element-23">
          <div className="lg:col-span-7 xl:col-span-8" data-id="element-24">
            <UpcomingJobsList data-id="element-25" />
          </div>
          <div className="lg:col-span-5 xl:col-span-4" data-id="element-26">
            <ActivityFeed data-id="element-27" />
          </div>
        </div>
      </>;
  };
  return <div className="min-h-screen bg-gray-50 font-inter text-gray-600" data-id="element-28">
      <Sidebar isCollapsed={sidebarCollapsed} toggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} activePage={activePage} onNavigate={setActivePage} userRole={userRole} onUserRoleChange={setUserRole} isMobileOpen={isMobileSidebarOpen} setIsMobileOpen={setIsMobileSidebarOpen} data-id="element-29" />

      {/* Mobile Header for Burger Menu */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-40 flex items-center px-4" data-id="element-30">
        <button onClick={() => setIsMobileSidebarOpen(true)} className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg" data-id="element-31">
          <Menu size={24} data-id="element-32" />
        </button>
        <div className="ml-4 font-bold text-navy font-montserrat" data-id="element-33">CleanAI</div>
      </div>

      <main className={`transition-all duration-300 min-h-screen flex flex-col pt-16 lg:pt-0 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`} data-id="element-34">
        {activePage === 'packages' || activePage === 'planner' || activePage === 'planner-map' ? <div className="flex-1 w-full h-[calc(100vh-64px)] lg:h-screen" data-id="element-35">
            {renderContent()}
          </div> : <div className="px-4 py-6 lg:px-6 lg:py-8 w-full" data-id="element-36">
            {renderContent()}
          </div>}
      </main>
    </div>;
}